import React, { useState } from "react";
import { toast } from "react-toastify";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import EditIcon from "@material-ui/icons/Edit";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import CancelIcon from "@material-ui/icons/Cancel";
import NotifyContactIcon from "@material-ui/icons/NotificationsActive";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from "../ButtonWithSpinner";
import TaskCompleteDialog from "./TaskCompleteDialog";
import TaskCancelDialog from "./TaskCancelDialog";
import TaskTransferDialog from "./TaskTransferDialog";
import NotifyTaskContactModal from "../NotifyTaskContactModal";
import {
  canEditTask,
  canStartTask,
  canCompleteTask,
  canTransferTask,
  canCancelTask,
  canNotifyTaskContact
} from "../../pages/Tasks/taskUtils";

const useStyles = makeStyles(theme => ({
  bar: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    width: "100%"
  },
  mobileBar: {
    flexDirection: "column",
    alignItems: "stretch"
  }
}));

// Todos os gatilhos de ação (editar/iniciar/concluir/transferir/cancelar)
// e os diálogos correspondentes ficam centralizados aqui — a visibilidade
// de cada botão vem das funções puras e centralizadas de taskUtils.js.
const TaskActionsBar = ({
  task,
  user,
  isMobile,
  isEditing,
  onEditToggle,
  onStart,
  onComplete,
  onTransfer,
  onCancel,
  onNotifyContactSuccess
}) => {
  const classes = useStyles();
  const theme = useTheme();
  // `primary.main` é configurável por empresa e pode ser quase preto.
  // Inline é intencional aqui: garante contraste acima das sobrescritas
  // globais de MuiButton usadas pelo layout no tema escuro.
  const darkPrimaryActionStyle = theme.palette.type === "dark"
    ? { color: theme.palette.common.white, borderColor: "rgba(255, 255, 255, 0.6)" }
    : undefined;
  const [startConfirmOpen, setStartConfirmOpen] = useState(false);
  const [startSaving, setStartSaving] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [notifyContactDialogOpen, setNotifyContactDialogOpen] = useState(false);

  const handleConfirmStart = async () => {
    if (startSaving) return;
    setStartSaving(true);
    const success = await onStart();
    setStartSaving(false);
    if (success) {
      toast.success(i18n.t("tasks.details.actions.startSuccess"));
      setStartConfirmOpen(false);
    }
  };

  const handleCloseStart = () => {
    if (!startSaving) setStartConfirmOpen(false);
  };

  const showEdit = canEditTask({ task, user }) && !isEditing;
  const showStart = canStartTask({ task, user });
  const showComplete = canCompleteTask({ task, user });
  const showTransfer = canTransferTask({ task, user });
  const showCancel = canCancelTask({ task, user });
  const showNotifyContact = canNotifyTaskContact({ task, user });

  if (!showEdit && !showStart && !showComplete && !showTransfer && !showCancel && !showNotifyContact) {
    return null;
  }

  return (
    <Box className={`${classes.bar} ${isMobile ? classes.mobileBar : ""}`}>
      {showEdit && (
        <Button
          variant="outlined"
          color="primary"
          style={darkPrimaryActionStyle}
          startIcon={<EditIcon />}
          fullWidth={isMobile}
          onClick={onEditToggle}
        >
          {i18n.t("tasks.details.actions.edit")}
        </Button>
      )}

      {showStart && (
        <Button
          variant="outlined"
          color="primary"
          style={darkPrimaryActionStyle}
          startIcon={<PlayArrowIcon />}
          fullWidth={isMobile}
          onClick={() => setStartConfirmOpen(true)}
        >
          {i18n.t("tasks.details.actions.start")}
        </Button>
      )}

      {showComplete && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<CheckCircleIcon />}
          fullWidth={isMobile}
          onClick={() => setCompleteDialogOpen(true)}
        >
          {i18n.t("tasks.details.actions.complete")}
        </Button>
      )}

      {showTransfer && (
        <Button
          variant="outlined"
          startIcon={<SwapHorizIcon />}
          fullWidth={isMobile}
          onClick={() => setTransferDialogOpen(true)}
        >
          {i18n.t("tasks.details.actions.transfer")}
        </Button>
      )}

      {showCancel && (
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<CancelIcon />}
          fullWidth={isMobile}
          onClick={() => setCancelDialogOpen(true)}
        >
          {i18n.t("tasks.details.actions.cancel")}
        </Button>
      )}

      {showNotifyContact && (
        <Button
          variant="outlined"
          color="primary"
          style={darkPrimaryActionStyle}
          startIcon={<NotifyContactIcon />}
          fullWidth={isMobile}
          onClick={() => setNotifyContactDialogOpen(true)}
        >
          {i18n.t("tasks.details.actions.notifyContact.button")}
        </Button>
      )}

      {startConfirmOpen && (
        <Dialog
          open={startConfirmOpen}
          onClose={handleCloseStart}
          aria-labelledby="task-start-dialog-title"
          disableBackdropClick={startSaving}
          disableEscapeKeyDown={startSaving}
        >
          <DialogTitle id="task-start-dialog-title">
            {i18n.t("tasks.details.actions.startTitle")}
          </DialogTitle>
          <DialogContent dividers>
            <Typography>{i18n.t("tasks.details.actions.startConfirm")}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseStart} disabled={startSaving} variant="outlined">
              {i18n.t("tasks.details.actions.cancelButton")}
            </Button>
            <ButtonWithSpinner
              onClick={handleConfirmStart}
              color="primary"
              variant="contained"
              loading={startSaving}
              disabled={startSaving}
            >
              {i18n.t("tasks.details.actions.start")}
            </ButtonWithSpinner>
          </DialogActions>
        </Dialog>
      )}

      {completeDialogOpen && (
        <TaskCompleteDialog
          open={completeDialogOpen}
          onClose={() => setCompleteDialogOpen(false)}
          onConfirm={onComplete}
        />
      )}

      {cancelDialogOpen && (
        <TaskCancelDialog
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
          onConfirm={onCancel}
        />
      )}

      {transferDialogOpen && (
        <TaskTransferDialog
          open={transferDialogOpen}
          onClose={() => setTransferDialogOpen(false)}
          onConfirm={onTransfer}
          currentAssignedUserId={task?.assignedUserId}
        />
      )}

      {notifyContactDialogOpen && (
        <NotifyTaskContactModal
          open={notifyContactDialogOpen}
          onClose={() => setNotifyContactDialogOpen(false)}
          task={task}
          onSuccess={onNotifyContactSuccess}
        />
      )}
    </Box>
  );
};

export default TaskActionsBar;
